import { omit } from 'Utility/ramdaReplacement';
import { ICERAD2023Computed, TDocument } from '../Document/definitions';
import { cutOffScoresByEducationYears } from '../Document/Form/CERAD/config';
import { arrayOfAll, sortPartialDate } from 'neuro-utils';

export const documentIsBackground = (d: TDocument): d is IBackground => d._type === 'background';

/**
 * Common function for calculating a task total from given keys of a CERAD document, all keys need to  have values for calculation
 * @param {ICERAD2023} d - CERAD document
 * @param {string[]} keys - Keys to calculate the total from
 * @returns {number | undefined} - Score if it could be calculated, else undefined
 */
const calculateCeradTaskTotal = (d: ICERAD2023, keys: string[]): number | undefined => {
  if (!d || typeof d !== 'object') return undefined;

  const values: number[] = [];

  for (const key of Object.keys(d)) {
    if (keys.includes(key) && typeof d[key as keyof typeof d] === 'number') {
      values.push(d[key as keyof typeof d] as number);
    }
  }

  if (values.length !== keys.length) return undefined;

  return values.reduce((prev, curr) => prev + curr, 0);
};

/**
 * Calculate word list learning task total
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - Total score for word list learning task if it could be calculated, else undefined
 */
export const calculateWordListLearningTotal = (d: ICERAD2023): number | undefined => {
  return calculateCeradTaskTotal(d, ['wordListTrial1', 'wordListTrial2', 'wordListTrial3']);
};

/**
 * Calculate copy figure task total
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - Total score for copy figure task if it could be calculated, else undefined
 */
export const calculateCopyFigureTotal = (d: ICERAD2023): number | undefined => {
  return calculateCeradTaskTotal(d, ['copyFigure1', 'copyFigure2', 'copyFigure3', 'copyFigure4']);
};

/**
 * 1) raakapisteet suhteutettuna tehtävän 4 kolmannen kierroksen tulokseen ja muutettuna prosenteiksi
 *
 * Calculates word list delayed recall retention percentage
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - The calculated retention percentage or undefined if it cannot be determined
 */
export const calculateWordListDelayedRecallRetentionPercentage = (d: ICERAD2023): number | undefined => {
  if (!d.wordListTrial3 || typeof d.wordListDelayedRecallRaw !== 'number') return undefined;
  return Math.round((d.wordListDelayedRecallRaw / d.wordListTrial3) * 100);
};

/**
 * Calculates word list recognition task total
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - The calculated total or undefined if it cannot be determined
 */
export const calculateWordListRecognitionTotal = (d: ICERAD2023): number | undefined => {
  return calculateCeradTaskTotal(d, ['wordListRecognitionYes', 'wordListRecognitionNo']);
};

/**
 * Calculates delayed figures task total
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - The calculated total or undefined if it cannot be determined
 */
export const calculateDelayedFiguresTotal = (d: ICERAD2023): number | undefined => {
  return calculateCeradTaskTotal(d, ['delayedFigure1', 'delayedFigure2', 'delayedFigure3', 'delayedFigure4']);
};

/**
 * 2) raakapisteet suhteutettuna tehtävän 5 raakapisteisiin ja muutettuna prosenteiksi
 *
 * Calculates delayed figures task retention percentage
 * @param {ICERAD2023} d - CERAD document
 * @returns {number | undefined} - The calculated retention percentage or undefined if it cannot be determined
 */
export const calculateDelayedFiguresRetentionPercentage = (d: ICERAD2023): number | undefined => {
  const copyFigureTotal = calculateCopyFigureTotal(d);
  const delayedFiguresTotal = calculateDelayedFiguresTotal(d);
  if (!copyFigureTotal || typeof delayedFiguresTotal !== 'number') return undefined;
  return Math.round((delayedFiguresTotal / copyFigureTotal) * 100);
};

type TCalculateTotalParams = [
  verbalFluency: number | undefined,
  naming: number | undefined,
  wordListLearningTotal: number | undefined,
  copyFigureTotal: number | undefined,
  wordListDelayedRecallRaw: number | undefined,
  wordListRecognitionYes: number | undefined,
  wordListRecognitionNo: number | undefined,
  delayedFiguresTotal: number | undefined,
];

/**
 * Kielellinen sujuvuus 0-24:
 * Jos tutkittava tuottaa yli 24 kategorian sanaa, kokonaispistemäärään lasketaan 24 pistettä
 *
 * Nimeämistehtävä 0-15
 *
 * Sanalistan oppiminen 0-30:
 * Kokonaispistemäärään lasketaan kolmen oppimiskierroksen summa
 *
 * Kopiointitehtävä 0-11
 *
 * Sanalistan viivästetty palautus 0-10:
 * Kokonaispistemäärään lasketaan palautettujen sanojen määrä, ei prosenttilukua
 *
 * Sanalistan tunnistaminen 0-10:
 * Kokonaispistemäärään oikein tunnistetuista sanoista eli oikeista kyllä-vastauksista vähennetään väärät kyllä-vastaukset
 *
 * Kuvioiden viivästetty mieleenpalautus 0-11
 *
 * ---
 *
 * Kokonaispisteet 0-111
 *
 * MMSE ja kellotehtävä eivät sisälly kokonaispistemäärään
 *
 * ---
 *
 * Calculates the CERAD total score based on provided parameters
 *
 * Overloads:
 * - Accepts multiple calculation parameters (`TCalculateTotalParams`)
 * - Accepts an optional `ICERAD2023` object
 *
 * @param {...TCalculateTotalParams} [params] - The calculation parameters (first overload) OR
 * @param {ICERAD2023} [d] - ICERAD2023 document (second overload)
 * @returns {number | undefined} - The calculated total or undefined if it cannot be determined
 */
export function calculateCeradTotal(...params: TCalculateTotalParams): number | undefined;
export function calculateCeradTotal(d?: ICERAD2023): number | undefined;
export function calculateCeradTotal(...args: [ICERAD2023?] | TCalculateTotalParams): number | undefined {
  let verbalFluency: number | undefined;
  let naming: number | undefined;
  let wordListLearningTotal: number | undefined;
  let copyFigureTotal: number | undefined;
  let wordListDelayedRecallRaw: number | undefined;
  let wordListRecognitionYes: number | undefined;
  let wordListRecognitionNo: number | undefined;
  let delayedFiguresTotal: number | undefined;

  if (args.length === 1 && typeof args[0] === 'object') {
    const d = args[0];

    verbalFluency = d.verbalFluency;
    naming = d.naming;
    wordListLearningTotal = calculateWordListLearningTotal(d);
    copyFigureTotal = calculateCopyFigureTotal(d);
    wordListDelayedRecallRaw = d.wordListDelayedRecallRaw;
    wordListRecognitionYes = d.wordListRecognitionYes;
    wordListRecognitionNo = d.wordListRecognitionNo;
    delayedFiguresTotal = calculateDelayedFiguresTotal(d);
  } else {
    [
      verbalFluency,
      naming,
      wordListLearningTotal,
      copyFigureTotal,
      wordListDelayedRecallRaw,
      wordListRecognitionYes,
      wordListRecognitionNo,
      delayedFiguresTotal,
    ] = args as TCalculateTotalParams;
  }

  // Used for total score sum
  const scores = [
    typeof verbalFluency === 'number' && verbalFluency > 24 ? 24 : verbalFluency,
    naming,
    wordListLearningTotal,
    copyFigureTotal,
    wordListDelayedRecallRaw,
    wordListRecognitionYes,
    delayedFiguresTotal,
  ].filter(score => typeof score === 'number');

  if (scores.length !== 7 || typeof wordListRecognitionNo !== 'number') return undefined;

  // scores[5] = wordListRecognitionYes = correct yes-answers
  // Here, incorrect yes-answers are subtracted from correct yes-answers, while clamping the result to not go below zero
  // Interpret that incorrect yes-answers = 10 - correct no-answers
  scores[5] = Math.max(0, scores[5] - (10 - wordListRecognitionNo));

  return scores.reduce((prev, curr) => prev + curr, 0);
}

/**
 * CERAD form control - Determine which computed data fields should update when doing changes in form
 * @param {string} task - Key of field which had its value changed
 * @returns {Array<keyof ICERAD2023Computed>} - Array of keys to determine which computed data fields should update
 */
export const getComputedDataThatShouldUpdate = (task: string): Array<keyof ICERAD2023Computed> => {
  switch (task) {
    case 'wordListTrial1':
    case 'wordListTrial2':
    case 'wordListTrial3':
      if (task === 'wordListTrial3') return ['wordListLearningTotal', 'wordListDelayedRecallRetentionPercentage'];
      return ['wordListLearningTotal'];
    case 'copyFigure1':
    case 'copyFigure2':
    case 'copyFigure3':
    case 'copyFigure4':
      return ['copyFigureTotal', 'delayedFiguresRetentionPercentage'];
    case 'wordListDelayedRecallRaw':
      return ['wordListDelayedRecallRetentionPercentage'];
    case 'wordListRecognitionYes':
    case 'wordListRecognitionNo':
      return ['wordListRecognitionTotal'];
    case 'delayedFigure1':
    case 'delayedFigure2':
    case 'delayedFigure3':
    case 'delayedFigure4':
      return ['delayedFiguresTotal', 'delayedFiguresRetentionPercentage'];
    default:
      return [];
  }
};

/**
 * CERAD form control - Get computed CERAD value, basically a selector for different value calculators
 * @param {ICERAD2023} d - CERAD document
 * @param {task} task - Key of computed value field for which to get the value
 * @returns {number | undefined} - The calculated value or undefined if it cannot be determined
 */
export const getComputedValue = (d: ICERAD2023, task: string): number | undefined => {
  switch (task) {
    case 'wordListLearningTotal':
      return calculateWordListLearningTotal(d);
    case 'copyFigureTotal':
      return calculateCopyFigureTotal(d);
    case 'wordListDelayedRecallRetentionPercentage':
      return calculateWordListDelayedRecallRetentionPercentage(d);
    case 'wordListRecognitionTotal':
      return calculateWordListRecognitionTotal(d);
    case 'delayedFiguresTotal':
      return calculateDelayedFiguresTotal(d);
    case 'delayedFiguresRetentionPercentage':
      return calculateDelayedFiguresRetentionPercentage(d);
    default:
      return undefined;
  }
};

/**
 * Get education years for a CERAD document either from background documents or from document data
 * @param {ICERAD2023} document - The CERAD document to return the education years for
 * @param {IBackground[]} backgroundDocuments - Array of background documents
 * @param {boolean | undefined} backgroundDocumentsAlreadyFiltered - Set as true if the background documents do not need filtering
 * @returns {'≤10' | '≥11' | undefined} - '≤10' if 10 or less education years have been recorded, else '≥11' if more have been recorded, else undefined
 */
export const getCeradEducationYears = (
  document: ICERAD2023,
  backgroundDocuments: IBackground[] = [],
  backgroundDocumentsAlreadyFiltered?: boolean,
): '≤10' | '≥11' | undefined => {
  if (!document || typeof document !== 'object') return;

  if (document.educationYears) return document.educationYears;

  const backgroundDocsWithEducationYears: IBackground[] = backgroundDocumentsAlreadyFiltered
    ? backgroundDocuments
    : backgroundDocuments.filter(
        d =>
          documentIsBackground(d) &&
          typeof d.educationYears === 'number' &&
          sortPartialDate(d.date, document.date) <= 0,
      );
  const latestEducationYears = backgroundDocsWithEducationYears[0]?.educationYears;

  return typeof latestEducationYears === 'number' ? (latestEducationYears < 11 ? '≤10' : '≥11') : undefined;
};

/**
 * Get updated computed CERAD data
 * @param {IFormData<ICERAD2023>['document']} doc - CERAD form data document
 * @param {IBackground[]} bgDocs - Array of background documents (to retrieve educationYears)
 * @param {boolean | undefined} bgDocsAlreadyFiltered - Set as true if the background documents do not need filtering
 * @returns {ICERAD2023} - Updated computed CERAD data
 */
export const getCeradUpdatedComputedData = (
  doc: IFormData<ICERAD2023>['document'],
  bgDocs?: IBackground[],
  bgDocsAlreadyFiltered?: boolean,
): ICERAD2023Computed => {
  let newComputedData: ICERAD2023Computed = {
    educationYears: getCeradEducationYears(doc, bgDocs, bgDocsAlreadyFiltered),
  };

  Object.keys(doc).forEach(t => {
    newComputedData = getComputedDataThatShouldUpdate(t).reduce(
      (current, key) => {
        const computedValue = getComputedValue(doc, key);
        current[key] = computedValue;
        return current;
      },
      newComputedData as { [key: string]: number | undefined },
    );
  });

  return newComputedData;
};

/**
 * Get cut-off score for CERAD total score based on education years
 * @param {'≤10' | '≥11' | undefined} educationYears - Education years value
 * @returns {number | undefined} - cut-off score for CERAD total or undefined if it cannot be determined
 */
export const getCeradTotalCutOffScore = (educationYears?: '≤10' | '≥11'): number | undefined => {
  if (typeof educationYears !== 'string') return undefined;
  const cutOffScores = cutOffScoresByEducationYears[educationYears];
  return cutOffScores?.total;
};

/**
 * Check if given CERAD score is abnormal based on cut-off score
 * @param {number | undefined} score - The score to check
 * @param {number | undefined} cutOffScore - The cut off score to base the comparison on
 * @returns {'normal' | 'abnormal' | undefined} - 'normal' for score above or equal to cutOffScore, 'abnormal' if below, else undefined
 * @note The 'normal' differentation is currently unused
 */
export const checkIfScoreIsAbnormal = (
  score: number | undefined,
  cutOffScore: number | undefined,
): 'normal' | 'abnormal' | undefined =>
  typeof score === 'number' && typeof cutOffScore === 'number'
    ? score < cutOffScore
      ? 'abnormal'
      : 'normal'
    : undefined;

/** Used to keep abnormal/unfilled tasks in right order */
const ceradTaskFields = arrayOfAll.keys<
  Omit<
    ICERAD2023 & ICERAD2023Computed,
    keyof IControlProps | 'version' | 'date' | 'educationYears' | 'additionalInformation' | 'totalScore'
  >
>()([
  'verbalFluency',
  'naming',
  'mmse',
  'wordListTrial1',
  'wordListTrial2',
  'wordListTrial3',
  'wordListLearningTotal',
  'copyFigure1',
  'copyFigure2',
  'copyFigure3',
  'copyFigure4',
  'copyFigureTotal',
  'wordListDelayedRecallRaw',
  'wordListDelayedRecallRetentionPercentage',
  'wordListRecognitionYes',
  'wordListRecognitionNo',
  'wordListRecognitionTotal',
  'delayedFigure1',
  'delayedFigure2',
  'delayedFigure3',
  'delayedFigure4',
  'delayedFiguresTotal',
  'delayedFiguresRetentionPercentage',
  'clockDrawing',
]);

const ceradTaskFieldsNotIncludedInTotal: typeof ceradTaskFields = [
  'mmse',
  'wordListDelayedRecallRetentionPercentage',
  'delayedFiguresRetentionPercentage',
  'clockDrawing',
];

/**
 * Get CERAD tasks that have not been filled (that are included total score calculation)
 * @param {ICERAD2023} document - CERAD document
 * @param {ICERAD2023Computed} computed - CERAD computed values
 * @returns {string[]} - Unfilled tasks that are included in total score calculation
 */
export const getCeradUnfilledTasks = (document: ICERAD2023, computed: ICERAD2023Computed): string[] => {
  const combined = {
    ...omit(['version', 'date', 'additionalInformation'], document),
    ...omit(['educationYears', 'totalScore'], computed),
  };

  return ceradTaskFields.filter(k => {
    const score = combined[k as keyof typeof combined];
    if (typeof score !== 'number') return !ceradTaskFieldsNotIncludedInTotal.includes(k);
    return false;
  });
};

/**
 * Get CERAD tasks that are abnormal (score is below cut-off score)
 * @param {ICERAD2023} document - CERAD document
 * @param {ICERAD2023Computed} computed - CERAD computed values
 * @returns {string[]} - Abnormal tasks
 */
export const getCeradAbnormalTasks = (document: ICERAD2023, computed: ICERAD2023Computed): string[] => {
  const combined = {
    ...omit(['version', 'date', 'additionalInformation'], document),
    ...omit(['educationYears', 'totalScore'], computed),
  };
  const abnormal: string[] = [];
  const cutOffScores = computed?.educationYears && cutOffScoresByEducationYears[computed.educationYears];

  Object.keys(combined).forEach(k => {
    const score = combined[k as keyof typeof combined];
    const cutOffScore = cutOffScores?.[k as keyof typeof cutOffScores];
    if (typeof score === 'number' && checkIfScoreIsAbnormal(score, cutOffScore) === 'abnormal') {
      abnormal.push(k);
    }
  });

  return ceradTaskFields.filter(f => abnormal.includes(f));
};
